import { z } from 'zod';

export const ActionFormSchema = z
  .object({
    action_type: z.string({ message: 'Action type is required' }),
    name: z.string({ message: 'Name is required' }),
    action_details: z
      .object({
        trigger: z.string().optional(),
        description: z.string().optional(),
        server_url: z.string().url({ message: 'Please enter a valid Server URL' }).optional(),
        timeout: z.coerce.number().min(10, { message: 'Minimum timeout is 10 seconds' }).optional(),
        server_token: z.string().optional(),
        target_number: z.union([z.string().optional(), z.number().optional()]),
        target_id: z.union([z.string().optional(), z.number().optional()]),
        target_type: z.string().optional(),
        target_value: z.any().optional(),
        transfer_mode: z.string().optional(),
        transfer_initiation_message: z.string().optional(),
        transfer_success_message: z.string().optional(),
        transfer_failure_message: z.string().optional(),
        human_availability_enabled: z.boolean().optional(),
        unavailable_message: z.string().optional(),
        availability_schedule: z
          .record(
            z.object({
              open: z.boolean().optional(),
              start_time: z.string().optional(),
              end_time: z.string().optional()
            })
          )
          .optional(),
        handoff_message: z
          .object({
            type: z.string().optional(),
            content: z.string().optional()
          })
          .optional(),
        message_content: z.string().optional(),
        agent_response: z.string().optional(),
        columns: z.array(z.string().optional()).optional(),
        notify_via: z.array(z.string().optional()).optional(),
        worksheet_id: z.union([z.string().optional(), z.number().optional()]),
        base_url: z.string().url({ message: 'Please enter a valid URL' }).optional(),
        exposed_parameters: z.string().optional(),
        http_method: z.string().optional(),
        verification_type: z.string().optional(),
        is_contact_authentication_required: z.any().optional(),
        integration_id: z.any().optional(),
        calendar_id: z.any().optional(),
        booking_slot: z.string().optional(),
        questionnaire: z
          .array(
            z.object({
              question: z.string({ message: 'Question is required.' })
            })
          )
          .optional(),
        headers: z.union([
          z
            .array(
              z
                .object({
                  field_name: z.string({ message: 'Field name is required.' }),
                  description: z.string({ message: 'description is required.' }),
                  value: z.string({ message: 'Value is required.' })
                })
                .optional()
            )
            .optional(),
          z
            .object({
              field_name: z.string({ message: 'Question is required.' }),
              description: z.string({ message: 'Description is required.' }),
              value: z.string({ message: 'Value is required.' }).optional()
            })
            .optional()
        ]),
        body_params: z
          .array(
            z
              .object({
                field_name: z.string({ message: 'Question is required.' }),
                description: z.string({ message: 'Question is required.' })
              })
              .optional()
          )
          .optional(),
        query_params: z
          .array(
            z
              .object({
                field_name: z.string({ message: 'Question is required.' }),
                description: z.string({ message: 'Question is required.' })
              })
              .optional()
          )
          .optional()
      })
      .optional()
  })
  .superRefine((values, ctx) => {
    if (values.action_type === 'transfer_call') {
      if (!values.action_details?.trigger) {
        ctx.addIssue({
          message: 'Trigger is required for transfer call',
          code: z.ZodIssueCode.custom,
          path: ['action_details', 'trigger']
        });
      }
      if (!values.action_details?.target_type) {
        ctx.addIssue({
          message: 'Target type is required for transfer call',
          code: z.ZodIssueCode.custom,
          path: ['action_details', 'target_type']
        });
      }
      if (!values.action_details?.target_value) {
        ctx.addIssue({
          message: 'Target value is required for transfer call',
          code: z.ZodIssueCode.custom,
          path: ['action_details', 'target_value']
        });
      }
      if (!values.action_details?.transfer_mode) {
        ctx.addIssue({
          message: 'Transfer mode is required for transfer call',
          code: z.ZodIssueCode.custom,
          path: ['action_details', 'transfer_mode']
        });
      }
    }

    if (values.action_type === 'send_sms') {
      if (!values.action_details?.message_content) {
        ctx.addIssue({
          message: 'Message content is required for sending SMS.',
          code: z.ZodIssueCode.custom,
          path: ['action_details', 'message_content']
        });
      }
    }

    if (values.action_type === 'integration') {
      if (!values.action_details?.integration_id) {
        ctx.addIssue({
          message: 'Integration ID is required for booking appointments.',
          code: z.ZodIssueCode.custom,
          path: ['action_details', 'integration', 'integration_id']
        });
      }
      // if (!values.action_details?.calendar_id) {
      //   ctx.addIssue({
      //     message: 'Calendar ID is required for booking appointments.',
      //     code: z.ZodIssueCode.custom,
      //     path: ['action_details', 'integration', 'calendar_id']
      //   });
      // }
      // if (!values.action_details?.booking_slot) {
      //   ctx.addIssue({
      //     message: 'Booking slot is required for booking appointments.',
      //     code: z.ZodIssueCode.custom,
      //     path: ['action_details', 'integration', 'booking_slot']
      //   });
      // }
    }

    // if (values.action_type === 'contact_authentication') {
    //   if (!values.action_details?.base_url) {
    //     ctx.addIssue({
    //       message: 'Base URL is required for contact authentication.',
    //       code: z.ZodIssueCode.custom,
    //       path: ['action_details', 'base_url']
    //     });
    //   }
    // }

    if (values.action_type === 'mcp_server') {
      if (!values.action_details?.server_url) {
        ctx.addIssue({
          message: 'Server URL is required for MCP Server action.',
          code: z.ZodIssueCode.custom,
          path: ['action_details', 'server_url']
        });
      }

      if (values.action_details?.timeout === undefined || values.action_details?.timeout < 10) {
        ctx.addIssue({
          message: 'Minimum timeout is 10 seconds for MCP Server action.',
          code: z.ZodIssueCode.custom,
          path: ['action_details', 'timeout']
        });
      }
    }
  });

export type ActionFormRequest = z.infer<typeof ActionFormSchema>;
